import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { isWebActive } from "@/lib/environment";
import {
  trackEvent,
  setUserId,
  setUserProperties,
  getGa4ClientId,
  getAccessChannel,
  getAppPlatform,
} from "@/lib/dataLayer";

export interface Profile {
  id: string;
  full_name: string | null;
  email: string | null;
  address: string | null;
  zip_code: string | null;
  city: string | null;
  phone: string | null;
  profile_completed: boolean | null;
  brickpoints: number | null;
  location_lat: number | null;
  location_lng: number | null;
  created_at?: string | null;
  // Stripe Identity (KYC) + mandato de tarjeta
  identity_required?: boolean | null;
  identity_verified?: boolean | null;
  identity_verified_at?: string | null;
  identity_biometric_verified?: boolean | null;
  identity_biometric_verified_at?: string | null;
  identity_verification_level?: "none" | "document" | "biometric" | string | null;
  identity_verification_session_id?: string | null;
  stripe_customer_id?: string | null;
  stripe_payment_method_id?: string | null;
  card_brand?: string | null;
  card_last4?: string | null;
  card_exp_month?: number | null;
  card_exp_year?: number | null;
  card_funding?: string | null;
  wallet_balance_cents?: number | null;
  role?: string | null;
  notify_unread_messages?: boolean | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  isLoading: boolean;
  isAdmin: boolean;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: Error | null; needsEmailConfirmation?: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updateUserPassword: (password: string) => Promise<{ error: Error | null }>;
  deleteUserAccount: () => Promise<{ error: Error | null }>;
  updateProfile: (data: Partial<Profile>) => Promise<{ error: Error | null }>;
  openAuthModal: (mode?: "login" | "signup" | "forgot-password", triggerSource?: string) => void;
  closeAuthModal: () => void;
  isAuthModalOpen: boolean;
  authModalMode: "login" | "signup" | "forgot-password";
  isComingSoonOpen: boolean;
  openComingSoonPopup: () => void;
  closeComingSoonPopup: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<"login" | "signup" | "forgot-password">("login");
  const [isComingSoonOpen, setIsComingSoonOpen] = useState(false);

  const fetchProfile = async (userId: string) => {
    console.log("[AuthContext] Fetching profile for:", userId);
    const { data, error } = await supabase
      .from("users")
      .select("*")
      .eq("id", userId)       // ✅ FIX: was "user_id", now "id" (column renamed in migration 20260527)
      .maybeSingle();

    if (error) {
      console.error("[AuthContext] Error fetching profile:", error);
    } else {
      console.log("[AuthContext] Profile fetched:", data);
      if (data) {
        setProfile(data as unknown as Profile);
        // Analytics: user properties (estado + canal de acceso) para segmentación GA4 / audiencias Ads
        setUserProperties({
          plan_tier: "p2p",
          user_status: (data as any).profile_completed ? "verified" : "lead",
          access_channel: getAccessChannel(),
          app_platform: getAppPlatform(),
        });
      } else {
        console.warn("[AuthContext] No profile found in 'users' table for this ID.");
      }
    }
  };

  /**
   * Analytics de autenticación. Distingue registro (sign_up) de login recurrente
   * comparando created_at con last_sign_in_at (margen 60s). Guarda user_id en GA4
   * y persiste el ga4_client_id en user_metadata para la conversión server-side.
   */
  const handleAuthAnalytics = async (authUser: User) => {
    try {
      const method = authUser.app_metadata?.provider === "google" ? "google" : "email";
      const created = new Date(authUser.created_at).getTime();
      const lastSignIn = new Date(
        authUser.last_sign_in_at ?? authUser.created_at
      ).getTime();
      const isFirstLogin = Math.abs(lastSignIn - created) < 60_000;

      if (isFirstLogin) {
        trackEvent("sign_up", { method });
      } else {
        trackEvent("login", { method });
      }

      // user_id estable (uuid) para atribución cross-device — nunca email
      setUserId(authUser.id);

      // Persistir client_id de GA4 y canal de acceso para el purchase server-side (Measurement Protocol)
      const clientId = await getGa4ClientId();
      const accessChannel = getAccessChannel();
      const needsMetadataUpdate =
        (clientId && authUser.user_metadata?.ga4_client_id !== clientId) ||
        authUser.user_metadata?.last_access_channel !== accessChannel;

      if (needsMetadataUpdate) {
        supabase.auth
          .updateUser({
            data: {
              ...(clientId ? { ga4_client_id: clientId } : {}),
              last_access_channel: accessChannel,
            },
          })
          .catch(() => { /* no bloquear el flujo por tracking */ });
      }
    } catch (err) {
      console.warn("[AuthContext] handleAuthAnalytics failed", err);
    }
  };

  const checkUserRoles = async (userId: string) => {
    // After Phase 2 migration: roles are stored directly in users.role column
    // ✅ GUARD: Ensure we have a valid session before querying (avoid 401 race condition)
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      console.warn("[AuthContext] checkUserRoles: No valid session token available, deferring role check");
      setIsAdmin(false);
      return;
    }

    const { data, error } = await supabase
      .from("users")
      .select("role")
      .eq("id", userId)       // ✅ FIX: was "user_id", now "id"
      .maybeSingle();

    if (error) {
      console.warn("[AuthContext] checkUserRoles error:", error);
      setIsAdmin(false);
    } else if (data?.role) {
      const userRole = data.role;
      setIsAdmin(userRole === "admin");
    } else {
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        // Defer Supabase calls with setTimeout
        if (session?.user) {
          setTimeout(() => {
            fetchProfile(session.user.id);
            checkUserRoles(session.user.id);
          }, 0);

          // ── Analytics: sign_up vs login + user_id + ga4_client_id ──
          if (event === "SIGNED_IN") {
            handleAuthAnalytics(session.user);
          }
        } else {
          setProfile(null);
          setIsAdmin(false);
          // Al cerrar sesión, limpiar el user_id de GA4
          if (event === "SIGNED_OUT") {
            setUserId(null);
          }
        }

        setIsLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        // Token de refresco inválido (ocurre en dev cuando la sesión local caduca
        // o el proyecto Supabase se reinicia). Limpiamos estado para evitar loops.
        console.warn("[AuthContext] getSession error (possible stale refresh token):", error.message);
        supabase.auth.signOut().catch(() => {});
        setSession(null);
        setUser(null);
        setProfile(null);
        setIsAdmin(false);
        setIsLoading(false);
        return;
      }

      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        fetchProfile(session.user.id);
        checkUserRoles(session.user.id);
      }

      setIsLoading(false);
    });

    // Set up periodic session validation (every 5 minutes)
    // This catches cases where auth.users.id exists but public.users.id doesn't (corrupted sessions)
    const validationInterval = setInterval(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        try {
          const { data: userExists } = await supabase
            .from('users')
            .select('id')               // ✅ FIX: was 'user_id', now 'id'
            .eq('id', session.user.id)   // ✅ FIX: was 'user_id', now 'id'
            .maybeSingle();

          if (!userExists) {
            console.error(
              '❌ [AuthContext] Session validation failed: id not found in public.users',
              'User Auth ID:', session.user.id,
              'This indicates a corrupted session. Forcing logout.'
            );
            
            // Force logout to clear corrupted session
            await supabase.auth.signOut();
            setUser(null);
            setSession(null);
            setProfile(null);
            setIsAdmin(false);
          }
        } catch (error) {
          console.error('❌ [AuthContext] Error during session validation:', error);
        }
      }
    }, 5 * 60 * 1000); // Run every 5 minutes

    return () => {
      subscription.unsubscribe();
      clearInterval(validationInterval);
    };
  }, []);

  const signUp = async (email: string, password: string, fullName?: string) => {
    const redirectUrl = `${window.location.origin}/?confirmed=email`;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        },
      },
    });

    // Detectar si se requiere confirmación de email:
    // - En dev (enable_confirmations = false): data.session no es null → usuario logueado inmediatamente
    // - En prod (enable_confirmations = true): data.session es null → usuario debe confirmar email
    const needsEmailConfirmation = data?.session === null;

    return { 
      error: error as Error | null,
      needsEmailConfirmation,
    };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { error: error as Error | null };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
      },
    });

    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setIsAdmin(false);
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/?type=recovery`,
    });
    return { error: error as Error | null };
  };

  const updateUserPassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    return { error: error as Error | null };
  };

  const deleteUserAccount = async () => {
    if (!user) return { error: new Error("No user logged in") };

    try {
      // Call the edge function that deactivates the account (soft-delete)
      const { data, error } = await supabase.functions.invoke('delete-user');

      if (error) {
        return { error: new Error(error.message || 'Failed to deactivate account') };
      }

      if (data?.error) {
        return { error: new Error(data.error) };
      }

      // Sign out and clear local state after successful deactivation
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      setProfile(null);
      setIsAdmin(false);

      return { error: null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  const updateProfile = async (data: Partial<Profile>) => {
    if (!user) return { error: new Error("No user logged in") };

    const { error } = await supabase
      .from("users")
      .update(data as never)
      .eq("id", user.id);    // ✅ FIX: was "user_id", now "id"

    if (!error) {
      await fetchProfile(user.id);
    }

    return { error: error as Error | null };
  };

  const openComingSoonPopup = () => {
    setIsComingSoonOpen(true);
  };

  const closeComingSoonPopup = () => {
    setIsComingSoonOpen(false);
  };

  const openAuthModal = (mode: "login" | "signup" | "forgot-password" = "login", triggerSource: string = "auth_modal") => {
    // Si la web no está activa (WEB_ACTIVE = false), limitamos el acceso público (login y signup)
    // redirigiendo al pop-up de waitlist, salvo que el usuario disponga de token de acceso beta.
    let hasBeta = false;
    try {
      hasBeta = localStorage.getItem("bs_beta_access") === "true";
    } catch {
      hasBeta = false;
    }

    if (!isWebActive() && !hasBeta) {
      setIsAuthModalOpen(false);
      setIsComingSoonOpen(true);
      return;
    }

    // Analytics: medir inicio del registro (fricción start vs. complete)
    if (mode === "signup") {
      trackEvent("bs_signup_start", { trigger_source: triggerSource });
    }
    setAuthModalMode(mode);
    setIsAuthModalOpen(true);
  };

  const closeAuthModal = () => {
    setIsAuthModalOpen(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        isLoading,
        isAdmin,
        signUp,
        signIn,
        signInWithGoogle,
        signOut,
        refreshProfile,
        resetPassword,
        updateUserPassword,
        deleteUserAccount,
        updateProfile,
        openAuthModal,
        closeAuthModal,
        isAuthModalOpen,
        authModalMode,
        isComingSoonOpen,
        openComingSoonPopup,
        closeComingSoonPopup,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};